import Cookies from 'js-cookie';
import { createContext, ProviderProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCESS_TOKEN_KEY } from 'src/constants/auth';
import { PermissionList } from 'src/enums/permission';
import { I_Agency, I_Profile } from 'src/models/admin';
import { apiRequest } from 'src/utils/api';
import { notification } from 'core-ui';

interface I_Context {
  profile?: I_Profile;
  fetchProfile?: (
    shouldShowLoading?: boolean
  ) => Promise<I_Profile | undefined>;
  isProfileLoading: boolean;
  hasRole: (role: PermissionList) => boolean;
  hasAnyOfRoles: (roles: PermissionList[]) => boolean;
  agency?: I_Agency;
}

export const ProfileContext = createContext<I_Context>({
  isProfileLoading: true,
  hasRole: () => true,
  hasAnyOfRoles: () => true
});

const ProfileProvider = (props: Partial<ProviderProps<unknown>>) => {
  const [profile, setProfile] = useState<I_Profile>();
  const [isProfileLoading, setProfileLoading] = useState(true);
  const [agency, setAgency] = useState<I_Agency>();
  const { t } = useTranslation();
  const fetchProfile = async (shouldShowLoading?: boolean) => {
    if (!Cookies.get(ACCESS_TOKEN_KEY)) {
      setProfileLoading(false);
      return;
    }
    shouldShowLoading && setProfileLoading(true);
    try {
      const { data } = await apiRequest.get({
        url: '/api/admin'
      });
      setProfile(data.data);
      setAgency({
        agencyId: data?.data?.agencyId,
        agencyName: data?.data?.agencyName
      });
      return data.data as I_Profile;
    } catch {
      notification.error({
        message: (
          <div className="whitespace-nowrap">
            {t('common.invalidMessage.serverUnavailable')}
          </div>
        ),
        style: { width: 'auto', maxWidth: 'none' }
      });
    } finally {
      setProfileLoading(false);
    }
  };
  const hasRole = (role: PermissionList) =>
    Boolean(profile?.permissions.find((i) => i === role));
  const hasAnyOfRoles = (roles: PermissionList[]) =>
    Boolean(profile?.permissions.some((i) => roles.includes(i)));
  return (
    <ProfileContext.Provider
      {...props}
      value={{
        profile,
        fetchProfile,
        isProfileLoading,
        hasRole,
        hasAnyOfRoles,
        agency
      }}
    />
  );
};
export default ProfileProvider;
